/**
 * I3M Calculations
 * 
 * Funzioni di calcolo per la stima dell'età basata sull'indice I3M.
 * Implementa il modello Bayesiano Gaussiano con interpolazione lineare.
 */

import { getLookupMap, getEthnicityParams, ethnicityData } from './probabilityTables';

// ============================================================================
// COSTANTI
// ============================================================================

/**
 * Soglia decisionale per la classificazione Adult/Minor
 * P(Adult) >= THRESHOLD → Adult
 * P(Adult) < THRESHOLD → Minor
 */
export const THRESHOLD = 0.5;

/**
 * Range valido per i valori I3M
 */
export const I3M_RANGE = {
  MIN: 0.00,
  MAX: 0.20,
};

// ============================================================================
// FUNZIONE PRINCIPALE: LOOKUP CON INTERPOLAZIONE
// ============================================================================

/**
 * Ottiene la probabilità P(Adult) per un dato valore I3M.
 * Utilizza interpolazione lineare per valori non esattamente tabulati.
 * 
 * Formula interpolazione:
 * P(x) = P_low + (x - x_low) / (x_high - x_low) × (P_high - P_low)
 * 
 * NOTA: Per valori I3M > 0.20, la probabilità di essere adulto è molto bassa
 * poiché un I3M alto indica un apice radicolare ancora aperto (tipico dei minori).
 * In questi casi, viene restituita una probabilità molto bassa (< 1%).
 * 
 * @param {number} i3m - Valore I3M (idealmente 0.00 - 0.20)
 * @param {string} region - Regione geografica (Europe, Asia, Africa, America)
 * @returns {object} { pAdult, pMinor, outOfRange, originalI3m }
 */
export function getProbability(i3m, region = 'Europe') {
  // Validazione input
  if (i3m === null || i3m === undefined || isNaN(i3m)) {
    return null;
  }

  // Flag per indicare se il valore è fuori range
  const outOfRange = i3m > I3M_RANGE.MAX;
  
  // Per valori I3M > 0.20, la probabilità di essere adulto è praticamente nulla
  // Un I3M alto indica un apice radicolare molto aperto → forte indicazione di minore
  if (i3m > I3M_RANGE.MAX) {
    // Calcolo esponenziale decrescente per valori oltre 0.20
    // A I3M = 0.20 abbiamo circa 0.7% (Europa), 70% (Asia), etc.
    // Oltre 0.20, la probabilità decresce rapidamente verso 0
    const lookup = getLookupMap(region);
    const maxValue = lookup.get(20) || 0.01; // P(Adult) a I3M=0.20
    
    // Decremento esponenziale: ogni 0.05 oltre 0.20, dimezziamo la probabilità
    const excessI3m = i3m - I3M_RANGE.MAX;
    const decayFactor = Math.pow(0.5, excessI3m / 0.05);
    const pAdult = Math.max(0.001, maxValue * decayFactor); // Minimo 0.1%
    
    return pAdult;
  }

  // Clamp al range valido (solo per i3m < 0)
  const clampedI3m = Math.max(I3M_RANGE.MIN, i3m);
  
  const lookup = getLookupMap(region);
  
  // Conversione a chiave intera (es. 0.085 → 8.5)
  const keyFloat = clampedI3m * 100;
  const keyRounded = Math.round(keyFloat);
  
  // CASO 1: Valore esatto trovato nella tabella
  const exact = lookup.get(keyRounded);
  if (exact !== undefined && Math.abs(keyFloat - keyRounded) < 0.0001) {
    return exact;
  }
  
  // CASO 2: Interpolazione lineare
  const lowerKey = Math.floor(keyFloat);
  const upperKey = Math.ceil(keyFloat);
  
  const lower = lookup.get(lowerKey);
  const upper = lookup.get(upperKey);
  
  if (lower !== undefined && upper !== undefined && lowerKey !== upperKey) {
    const ratio = (keyFloat - lowerKey) / (upperKey - lowerKey);
    return lower + ratio * (upper - lower);
  }
  
  // CASO 3: Fallback - usa il valore più vicino
  if (lower !== undefined) return lower;
  if (upper !== undefined) return upper;
  
  // Default: probabilità neutra
  return 0.5;
}

/**
 * Ottiene P(Minor) = 1 - P(Adult)
 * 
 * @param {number} i3m - Valore I3M
 * @param {string} region - Regione geografica
 * @returns {number} Probabilità P(Minor) tra 0 e 1
 */
export function getProbabilityMinor(i3m, region = 'Europe') {
  const pAdult = getProbability(i3m, region);
  return pAdult !== null ? 1 - pAdult : null;
}

// ============================================================================
// DECISIONE COMBINATA (ENTRAMBI I DENTI)
// ============================================================================

/**
 * Calcola la decisione combinata quando sono disponibili entrambi i denti (I3R e I3L).
 * Implementa sia la decisione basata sulla media che il principio di massima protezione.
 * 
 * Principio di Massima Protezione:
 * - Se ALMENO UNO dei due denti indica P(Adult) < 50% → classificazione MINORE
 * - Minimizza l'errore di classificare un minore come adulto
 * 
 * @param {number|null} i3r - Valore I3M del terzo molare destro
 * @param {number|null} i3l - Valore I3M del terzo molare sinistro
 * @param {string} region - Regione geografica
 * @returns {Object} Risultato dell'analisi
 */
export function combinedDecision(i3r, i3l, region = 'Europe') {
  // Lookup probabilità individuali
  const p1 = i3r !== null && !isNaN(i3r) ? getProbability(i3r, region) : null;
  const p2 = i3l !== null && !isNaN(i3l) ? getProbability(i3l, region) : null;
  
  let probMedia;
  let decisionMedia;
  let decisionMaxProtection;
  
  // SCENARIO A: Entrambi i valori disponibili
  if (p1 !== null && p2 !== null) {
    // Media aritmetica delle probabilità
    probMedia = (p1 + p2) / 2;
    
    // Decisione basata sulla media
    decisionMedia = probMedia >= THRESHOLD ? 'Adult' : 'Minor';
    
    // PRINCIPIO DI MASSIMA PROTEZIONE:
    // Se ALMENO UNO dei due denti indica minorenne → Minor
    decisionMaxProtection = (p1 < THRESHOLD || p2 < THRESHOLD) ? 'Minor' : 'Adult';
  }
  // SCENARIO B: Solo I3R disponibile
  else if (p1 !== null) {
    probMedia = p1;
    decisionMedia = p1 >= THRESHOLD ? 'Adult' : 'Minor';
    decisionMaxProtection = decisionMedia; // Stesso valore (no confronto possibile)
  }
  // SCENARIO C: Solo I3L disponibile
  else if (p2 !== null) {
    probMedia = p2;
    decisionMedia = p2 >= THRESHOLD ? 'Adult' : 'Minor';
    decisionMaxProtection = decisionMedia; // Stesso valore
  }
  // SCENARIO D: Nessun valore disponibile
  else {
    return {
      isValid: false,
      error: 'No valid I3M values provided',
      probMedia: null,
      decisionMedia: 'Invalid',
      decisionMaxProtection: 'Invalid',
      p1: null,
      p2: null,
    };
  }
  
  // Calcola concordanza tra le due decisioni
  const decisionsAgree = decisionMedia === decisionMaxProtection;
  
  return {
    isValid: true,
    // Valori I3M originali
    valueI3R: i3r,
    valueI3L: i3l,
    // Probabilità individuali
    p1,
    p2,
    // Probabilità media
    probMedia,
    // Decisioni
    decisionMedia,
    decisionMaxProtection,
    // Concordanza
    decisionsAgree,
    // Metadati regione
    region,
    ethnicity: getEthnicityParams(region),
  };
}

// ============================================================================
// CONFRONTO CON GOLDEN STANDARD
// ============================================================================

/**
 * Confronta l'analisi etnia-specifica con il Golden Standard (Europa/default).
 * Utile per valutare quanto l'approccio etnia-specifico differisce da quello universale.
 * 
 * @param {number|null} i3r - Valore I3M destro
 * @param {number|null} i3l - Valore I3M sinistro
 * @param {string} region - Regione etnica specifica
 * @returns {Object} Confronto tra analisi etnica e Golden Standard
 */
export function getGoldenStandardComparison(i3r, i3l, region) {
  // Analisi con parametri etnia-specifici
  const ethnicAnalysis = combinedDecision(i3r, i3l, region);
  
  // Analisi con Golden Standard (Europa/Unknown)
  const goldenAnalysis = combinedDecision(i3r, i3l, 'Unknown');
  
  // Verifica concordanza tra i due approcci
  const agreement = ethnicAnalysis.decisionMedia === goldenAnalysis.decisionMedia;
  const maxProtectionAgreement = ethnicAnalysis.decisionMaxProtection === goldenAnalysis.decisionMaxProtection;
  
  // Calcola la differenza di probabilità
  const probDifference = ethnicAnalysis.probMedia !== null && goldenAnalysis.probMedia !== null
    ? ethnicAnalysis.probMedia - goldenAnalysis.probMedia
    : null;
  
  return {
    // Analisi etnia-specifica
    ethnic: {
      region: region,
      probMedia: ethnicAnalysis.probMedia,
      p1: ethnicAnalysis.p1,
      p2: ethnicAnalysis.p2,
      decisionMedia: ethnicAnalysis.decisionMedia,
      decisionMaxProtection: ethnicAnalysis.decisionMaxProtection,
    },
    // Analisi Golden Standard
    goldenStandard: {
      region: 'Golden Standard',
      probMedia: goldenAnalysis.probMedia,
      p1: goldenAnalysis.p1,
      p2: goldenAnalysis.p2,
      decisionMedia: goldenAnalysis.decisionMedia,
      decisionMaxProtection: goldenAnalysis.decisionMaxProtection,
    },
    // Confronto
    comparison: {
      agreement,
      maxProtectionAgreement,
      probDifference,
      probDifferencePercent: probDifference !== null ? (probDifference * 100).toFixed(1) : null,
    },
  };
}

// ============================================================================
// ANALISI COMPLETA
// ============================================================================

/**
 * Esegue un'analisi completa per un caso I3M.
 * Combina tutte le funzionalità: probabilità, decisioni, confronto Golden Standard.
 * 
 * @param {Object} params - Parametri dell'analisi
 * @param {number|null} params.i3r - Valore I3M destro
 * @param {number|null} params.i3l - Valore I3M sinistro
 * @param {string} params.region - Regione geografica
 * @param {string} [params.gender] - Genere (M/F) - per riferimento
 * @returns {Object} Analisi completa
 */
export function analyzeI3M({ i3r, i3l, region, gender = null }) {
  // Decisione combinata principale
  const mainAnalysis = combinedDecision(i3r, i3l, region);
  
  if (!mainAnalysis.isValid) {
    return mainAnalysis;
  }
  
  // Confronto con Golden Standard
  const goldenComparison = getGoldenStandardComparison(i3r, i3l, region);
  
  // Parametri etnia
  const ethnicityParams = getEthnicityParams(region);
  
  // Determina il livello di confidenza basato sulla probabilità
  let confidenceLevel;
  const prob = mainAnalysis.probMedia;
  if (prob >= 0.90 || prob <= 0.10) {
    confidenceLevel = 'High';
  } else if (prob >= 0.70 || prob <= 0.30) {
    confidenceLevel = 'Moderate';
  } else {
    confidenceLevel = 'Low';
  }
  
  return {
    // Dati input
    input: {
      i3r,
      i3l,
      region,
      gender,
    },
    // Risultati principali
    results: {
      p1: mainAnalysis.p1,
      p2: mainAnalysis.p2,
      probMedia: mainAnalysis.probMedia,
      probMediaPercent: (mainAnalysis.probMedia * 100).toFixed(1),
      decisionMedia: mainAnalysis.decisionMedia,
      decisionMaxProtection: mainAnalysis.decisionMaxProtection,
      decisionsAgree: mainAnalysis.decisionsAgree,
      confidenceLevel,
    },
    // Informazioni etnia
    ethnicity: {
      name: ethnicityParams.name,
      label: ethnicityParams.label,
      flag: ethnicityParams.flag,
      cutoff: ethnicityParams.cutoff,
      sensitivity: ethnicityParams.sensitivity,
      specificity: ethnicityParams.specificity,
    },
    // Confronto Golden Standard
    goldenStandardComparison: goldenComparison,
    // Timestamp
    timestamp: new Date().toISOString(),
  };
}

// ============================================================================
// FUNZIONI HELPER
// ============================================================================

/**
 * Formatta la probabilità come percentuale
 * 
 * @param {number} probability - Probabilità (0-1)
 * @param {number} decimals - Decimali da mostrare
 * @returns {string} Probabilità formattata (es. "82.5%")
 */
export function formatProbability(probability, decimals = 1) {
  if (probability === null || probability === undefined) {
    return 'N/A';
  }
  return `${(probability * 100).toFixed(decimals)}%`;
}

/**
 * Determina il colore da usare per visualizzare una probabilità
 * 
 * @param {number} probability - Probabilità P(Adult)
 * @returns {string} Colore CSS (green, orange, red)
 */
export function getProbabilityColor(probability) {
  if (probability === null) return 'gray';
  if (probability >= 0.7) return 'green';
  if (probability >= 0.5) return 'orange';
  if (probability >= 0.3) return 'yellow';
  return 'red';
}

/**
 * Genera un messaggio descrittivo per la classificazione
 * 
 * @param {Object} analysis - Risultato dell'analisi
 * @returns {string} Messaggio descrittivo
 */
export function getClassificationMessage(analysis) {
  if (!analysis || !analysis.results) {
    return 'Analysis not available';
  }
  
  const { decisionMedia, decisionMaxProtection, decisionsAgree, probMediaPercent } = analysis.results;
  
  if (decisionsAgree) {
    return `Classification: ${decisionMedia} (${probMediaPercent}% probability of being adult)`;
  } else {
    return `Classification: ${decisionMaxProtection} (Max Protection) - Average suggests ${decisionMedia} (${probMediaPercent}%), but one tooth indicates Minor`;
  }
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  THRESHOLD,
  I3M_RANGE,
  getProbability,
  getProbabilityMinor,
  combinedDecision,
  getGoldenStandardComparison,
  analyzeI3M,
  formatProbability,
  getProbabilityColor,
  getClassificationMessage,
};
